// Backwards-compat shim. The canonical source of truth is now the Hiveku CMS:
//   content/services/<slug>.json (typed by ServiceEntry in @/lib/cms).
//
// Each JSON file ships with `name, subtitle, description, conditions, order`.
// This file imports them statically so the existing `makeServiceT(name, t)`
// API used by Pricing / Businesses / Individuals / etc. keeps working,
// while edits flow through the CMS dashboard, not code.

import behavioralHealth from "../../content/services/behavioral-health.json";
import bestPriceMri from "../../content/services/best-price-mri-imaging.json";
import dermatology from "../../content/services/dermatology.json";
import healthcareConcierge from "../../content/services/healthcare-concierge.json";
import labTesting from "../../content/services/lab-testing.json";
import medicalBillFinancing from "../../content/services/medical-bill-financing.json";
import medicalBillNegotiations from "../../content/services/medical-bill-negotiations.json";
import pharmacySavings from "../../content/services/pharmacy-savings.json";
import telemedicine from "../../content/services/telemedicine.json";
import wellnessCoaching from "../../content/services/wellness-coaching.json";

export interface Service {
  name: string;
  subtitle: string;
  description: string;
  conditions?: string;
}

type ServiceCmsEntry = {
  name: string;
  subtitle?: string;
  description: string;
  conditions?: string;
  order?: number;
  status?: string;
};

const CMS_SERVICES: ServiceCmsEntry[] = [
  telemedicine,
  dermatology,
  behavioralHealth,
  pharmacySavings,
  healthcareConcierge,
  labTesting,
  wellnessCoaching,
  bestPriceMri,
  medicalBillNegotiations,
  medicalBillFinancing,
];

export const serviceDetails: Record<string, Omit<Service, "name">> = Object.fromEntries(
  CMS_SERVICES.map((s) => [
    s.name,
    {
      subtitle: s.subtitle ?? "",
      description: s.description,
      conditions: s.conditions || undefined,
    },
  ]),
);

export const makeService = (name: string): Service => ({
  name,
  ...serviceDetails[name],
});

// Maps service name → i18n key under "services.*". Kept for translation overlay.
const serviceKeyMap: Record<string, string> = {
  Telemedicine: "telemedicine",
  Dermatology: "dermatology",
  "Behavioral Health": "behavioralHealth",
  "Pharmacy Savings": "pharmacySavings",
  "Healthcare Concierge": "healthcareConcierge",
  "Lab Testing": "labTesting",
  "Wellness Coaching": "wellnessCoaching",
  "Best Price MRI/Imaging": "bestPriceMRI",
  "Medical Bill Negotiations": "medicalBillNegotiations",
  "Medical Bill Financing": "medicalBillFinancing",
};

export const makeServiceT = (name: string, t: (key: string, opts?: Record<string, unknown>) => string): Service => {
  const base = serviceDetails[name];
  const key = serviceKeyMap[name];
  if (!key || !base) return { name, ...(base ?? { subtitle: "", description: "" }) };
  return {
    name: t(`services.${key}.name`, { defaultValue: name }),
    subtitle: t(`services.${key}.subtitle`, { defaultValue: base.subtitle }),
    description: t(`services.${key}.description`, { defaultValue: base.description }),
    conditions: base.conditions
      ? t(`services.${key}.conditions`, { defaultValue: base.conditions })
      : undefined,
  };
};
